import React, { useState, useEffect, useRef } from 'react';
// Logger removed - using inline conditions for production builds
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import SpecialResponseRenderer, { hasSpecialResponseMarkers } from '../../components/Assistant/SpecialResponseRenderer';
import {
  getAssistantId,
  getVectorStoreId,
  createChatSession,
  updateChatSession,
  createThread,
  addMessageToThread,
  runAssistant,
  checkRunStatus,
  listMessages,
  saveChatMessage,
} from '../../api';
import { setupAssistantPrompt } from './utilities';
import { getToolsArray } from './functionCalls';
import { TypingIndicator } from '../../ui';
import reisloSymbol from '../../img/logo-green-symbol.svg';
import { UserIcon, PaperAirplaneIcon } from '@heroicons/react/24/solid';

const Chat = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [threadId, setThreadId] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem('directus_token') || sessionStorage.getItem('directus_token');
    if (!token) {
      navigate('/login');
    }
  }, [navigate]);

  const handleSendMessage = async () => {
    if (!input.trim()) return;

    setLoading(true);

    try {
      let currentThreadId = threadId;
      let currentSessionId = sessionId;

      const assistantId = await getAssistantId();
      const vectorStoreId = await getVectorStoreId();

      if (!currentThreadId) {
        // Create a new thread
        currentThreadId = await createThread();
        setThreadId(currentThreadId);
        
        // Add this line to test the prompt setup
        await setupAssistantPrompt(currentThreadId);

        // Create a new chat session in Directus (only once per thread)
        try {
          const newSession = await createChatSession(
            'New Chat',
            currentThreadId,
            assistantId,
            vectorStoreId
          );
          currentSessionId = newSession.id;
          setSessionId(currentSessionId);
        } catch (sessionError) {
          process.env.NODE_ENV === "development" && console.error('Error creating chat session:', sessionError);
          // Continue execution even if session creation fails
        }
      }

      // Add user message to the thread (OpenAI)
      await addMessageToThread(currentThreadId, {
        role: 'user',
        content: input,
      });

      // Save user message to Directus
      await saveChatMessage(currentSessionId, {
        role: 'user',
        content: input,
        order: messages.length
      });

      setMessages(prevMessages => [...prevMessages, { text: input, sender: 'user' }]);
      setInput('');

      // Get tools array with both file_search and function calling
      const tools = await getToolsArray(true);

      // Run the assistant with the current Vector Store ID
      const runId = await runAssistant(currentThreadId, assistantId, {
        tools: tools,
        file_search: { vector_store_ids: [vectorStoreId] }
      });


      // Check run status
      let runStatus;
      do {
        const runData = await checkRunStatus(currentThreadId, runId);
        runStatus = runData.status;
        await new Promise(resolve => setTimeout(resolve, 2000));
      } while (runStatus === 'in_progress');

      // Fetch messages from OpenAI
      const messagesRes = await listMessages(currentThreadId);
      const assistantMessage = messagesRes.data.find(msg => msg.role === 'assistant');

      if (assistantMessage) {
        const assistantResponse = assistantMessage.content[0].text.value;
        
        // Save assistant message to Directus
        await saveChatMessage(currentSessionId, {
          role: 'assistant',
          content: assistantResponse,
          order: messages.length + 1
        });

        setMessages(prevMessages => [...prevMessages, { text: assistantResponse, sender: 'assistant' }]);
      }

      // Update chat session title in Directus
      if (currentSessionId) {
        try {
          await updateChatSession(currentSessionId, { 
            title: input.slice(0, 50) + '...',
            date_updated: new Date().toISOString()
          });
        } catch (updateError) {
          process.env.NODE_ENV === "development" && console.error('Error updating chat session:', updateError);
          // Continue execution even if update fails
        }
      } else {
        process.env.NODE_ENV === "development" && console.error('No session ID available for update');
      }

    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Error in chat process:', error);
      if (error.response) {

      }
      setMessages(prevMessages => [...prevMessages, { text: "Sorry, I encountered an error. Please try again.", sender: 'assistant' }]);
    } finally {
      setLoading(false);
    }
  };

  const renderMessage = (message) => {
    if (hasSpecialResponseMarkers(message.text)) {
      return <SpecialResponseRenderer content={message.text} />;
    }

    return (
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: ({ children, ...props }) => <h1 className="text-xl font-bold mt-4 mb-2" {...props}>{children}</h1>,
          h2: ({ children, ...props }) => <h2 className="text-lg font-bold mt-4 mb-2" {...props}>{children}</h2>,
          h3: ({ children, ...props }) => <h3 className="text-md font-bold mt-4 mb-2" {...props}>{children}</h3>,
          h4: ({ children, ...props }) => <h4 className="text-sm font-bold mt-3 mb-2" {...props}>{children}</h4>,
          h5: ({ children, ...props }) => <h5 className="text-sm font-semibold mt-3 mb-2" {...props}>{children}</h5>,
          h6: ({ children, ...props }) => <h6 className="text-sm font-medium mt-3 mb-2" {...props}>{children}</h6>,
          strong: ({ ...props }) => <strong className="font-bold" {...props} />,
          em: ({ ...props }) => <em className="italic" {...props} />,
          ul: ({ ...props }) => <ul className="list-disc list-inside my-2 pl-4" {...props} />,
          ol: ({ ...props }) => <ol className="list-decimal list-inside my-2 pl-4" {...props} />,
          li: ({ ...props }) => <li className="mb-1" {...props} />,
          table: ({ ...props }) => (
            <div className="overflow-x-auto my-4 w-full">
              <table className="min-w-full table-auto border-collapse border border-gray-300 bg-white rounded-lg" {...props} />
            </div>
          ),
          thead: ({ ...props }) => <thead className="bg-gray-50" {...props} />,
          tbody: ({ ...props }) => <tbody {...props} />,
          tr: ({ ...props }) => <tr className="border-b border-gray-200 hover:bg-gray-50" {...props} />,
          th: ({ ...props }) => (
            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border border-gray-300" {...props} />
          ),
          td: ({ ...props }) => (
            <td className="px-4 py-3 text-sm text-gray-700 border border-gray-300 align-top" {...props} />
          ),
          a: ({ children, ...props }) => (
            <a className="text-blue-600 hover:text-blue-800 underline font-medium transition-colors duration-200" target="_blank" rel="noopener noreferrer" {...props}>{children}</a>
          ),
          code: ({ ...props }) => (
            <code className="bg-gray-100 text-gray-800 px-2 py-1 rounded text-sm font-mono" {...props} />
          ),
          blockquote: ({ ...props }) => (
            <blockquote className="border-l-4 border-gray-300 pl-4 italic my-4 text-gray-600" {...props} />
          ),
          p: ({ node, ...props }) => {
            const children = React.Children.map(props.children, child => {
              if (typeof child === 'string') {
                return child.split(/(【\d+:\d+†[^】]+】)/g).map((part, index) => {
                  if (part.match(/【\d+:\d+†[^】]+】/)) {
                    const source = part.slice(1, -1);
                    return (
                      <span
                        key={index}
                        className="text-primary-500 cursor-pointer hover:underline ml-1 text-xs"
                        title={source}
                      >
                        [Source]
                      </span>
                    );
                  }
                  return part;
                });
              }
              return child;
            });
            return <p className="mb-2" {...props}>{children}</p>;
          }
        }}
      >
        {message.text}
      </ReactMarkdown>
    );
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  return (
    <div className="flex flex-col h-screen pr-8 bg-neutral-50">
      <div className="max-w-5xl mx-auto pb-48 pt-8 w-full">
        <div className="flex-1 p-4 w-full">
          <div className="p-4 rounded mb-4 h-full overflow-y-auto w-full">
            {messages.map((message, index) => (
              <div key={index} className="mb-4 w-full pt-2">
                <div className="flex items-start w-full">
                  <div className="flex-shrink-0 mr-4">
                    {message.sender === 'user' ? (
                      <div className="w-8 h-8 rounded-full bg-secondary-50 flex items-center justify-center">
                        <UserIcon className="w-5 h-5 text-secondary-500" />
                      </div>
                    ) : (
                      <img
                        src={reisloSymbol}
                        alt="AI avatar"
                        className="w-8 h-8 rounded-full bg-primary-100 p-1.5 mt-0"
                      />
                    )}
                  </div>
                  <div className="flex-grow pt-1 prose max-w-none">
                    {renderMessage(message)}
                  </div>
                </div>
              </div>
            ))}
            {loading && (
              <div className="mb-2 w-full">
                <div className="flex items-start w-full">
                  <div className="flex-shrink-0 mr-2">
                    <img
                      src={reisloSymbol}
                      alt="AI avatar"
                      className="w-8 h-8 rounded-full bg-gray-200 p-1"
                    />
                  </div>
                  <div className="flex-grow">
                    <TypingIndicator />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        </div>
        <div className="w-full left-0 mx-auto fixed bottom-8">
          <div className="max-w-5xl mx-auto border rounded-xl bg-white p-4 w-full"> 
            <div className="flex w-full">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                className="flex-grow border p-2 rounded mr-2"
                placeholder={t('assistant.typeMessage')}
              />
              <button
                onClick={handleSendMessage}
                className="bg-primary-500 flex h-full text-white p-4 rounded relative hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-opacity-50 active:bg-primary-700 transition duration-150 ease-in-out"
                disabled={loading}
              >
                {loading ? 'Waiting...' : (
                  <>
                    Send
                    <PaperAirplaneIcon className="w-5 h-5 ml-2 inline-block" />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Chat;
